import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db, collection, getDoc, getDocs, doc, writeBatch } from '../firebase';
import { GiTennisBall } from 'react-icons/gi';
import { FaDownload } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Helmet } from 'react-helmet';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { leagueToolsTranslations } from '../i18n/translations';

const CalendarLeague = () => {
  const { leagueId } = useParams();
  const { language } = useLanguage();
  const t = createTranslator(language, leagueToolsTranslations);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [participantsLimit, setParticipantsLimit] = useState(0);
  const [leagueInfo, setLeagueInfo] = useState({});
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [collapsedWeeks, setCollapsedWeeks] = useState([]); // For collapsing weeks
  const [registeredTeamsCount, setRegisteredTeamsCount] = useState(0); // État pour le nombre d'équipes inscrites
  const callLeagueCalendarFunction = async (params) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `https://us-central1-mendyx-adafe.cloudfunctions.net/leagueCalendarFunctions-leagueCalendarFunction?${queryString}`;
  
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error calling Cloud Function: ", error);
      throw error;
    }
  };

  const saveMatchesViaCloudFunction = async (matches) => {
    const url = `https://us-central1-mendyx-adafe.cloudfunctions.net/leagueCalendarFunctions-leagueCalendarFunction`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'saveMatches',
          leagueId: leagueId,
          matches: matches
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ Matchs sauvegardés:', result);
      return result;
    } catch (error) {
      console.error("❌ Erreur lors de la sauvegarde:", error);
      throw error;
    }
  };
  

  useEffect(() => {

    
  

    const fetchLeagueInfo = async () => {
      try {
        const data = await callLeagueCalendarFunction({ action: 'getLeagueInfo', leagueId });
        setLeagueInfo({
          location: data.location || 'Lieu à définir',
          dateRange: data.dateRange || [],
          time: data.time || '18:00',
          players: data.players,
          terrain1: data.terrain1,
          terrain2: data.terrain2
        });
        setParticipantsLimit(data.players || 4);
      } catch (error) {
        console.error("Error fetching league info: ", error);
      }
    };
    
    fetchLeagueInfo();
  }, [leagueId]);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const data = await callLeagueCalendarFunction({ action: 'getLeagueTeams', leagueId });
        const fetchedTeams = data.map((team) => team.teamName);
        setTeams(fetchedTeams);
        setRegisteredTeamsCount(fetchedTeams.length);
      } catch (error) {
        console.error("Error fetching teams: ", error);
      }
    };
    

    fetchTeams();
  }, [leagueId]);

  useEffect(() => {
    if (teams.length === participantsLimit && participantsLimit > 0) {
      console.log(
        `Génération du calendrier : ${teams.length}/${participantsLimit} équipes inscrites.`
      );
      checkAndGenerateMatches();
    } else {
      console.log(
        `Attente : ${teams.length}/${participantsLimit} équipes inscrites.`
      );
    }
  }, [teams, participantsLimit]);
  

  useEffect(() => {
    groupMatchesByWeek();
  }, [matches]);

  const toggleWeekCollapse = (weekIndex) => {
    setCollapsedWeeks((prev) => {
      const newCollapsedWeeks = [...prev];
      newCollapsedWeeks[weekIndex] = !newCollapsedWeeks[weekIndex];
      return newCollapsedWeeks;
    });
  };
  const checkAndGenerateMatches = async () => {
    if (teams.length !== participantsLimit || participantsLimit === 0) {
      console.log(`Attente : ${teams.length}/${participantsLimit} équipes inscrites.`);
      return;
    }
  
    try {
      // Vérifie s'il y a déjà des matchs via Cloud Function
      const existingMatches = await callLeagueCalendarFunction({ 
        action: 'getLeagueMatches', 
        leagueId 
      });
  
      if (existingMatches && existingMatches.length > 0) {
        console.log('✅ Matchs existants trouvés:', existingMatches.length);
        const sortedMatches = existingMatches.sort((a, b) => new Date(a.date) - new Date(b.date));
        setMatches(sortedMatches);
      } else {
        console.log('🔄 Génération des matchs en cours...');
        await generateMatchesLocally();
      }
    } catch (error) {
      console.error('❌ Erreur lors de la vérification des matchs:', error);
    }
  };

  const generateMatchesLocally = async () => {
    if (teams.length < 2) {
      console.error("Pas assez d'équipes pour générer des matchs.");
      return;
    }
  
    try {
      // 🔍 Récupère les détails des équipes via Cloud Function
      const teamDetails = await callLeagueCalendarFunction({ 
        action: 'getLeagueTeams', 
        leagueId 
      });
  
      // Convertit en objet pour faciliter l'accès
      const teamDetailsMap = {};
      teamDetails.forEach((team) => {
        teamDetailsMap[team.teamName] = team;
      });
  
      const newMatches = [];
      const [startDate] = leagueInfo.dateRange.map((date) => new Date(date));
      let terrainToggle = true;
      let matchDate = new Date(startDate);
  
      const teamList = [...teams];
      if (teamList.length % 2 !== 0) {
        teamList.push("BYE");
      }
  
      const totalRounds = teamList.length - 1;
      const halfSize = teamList.length / 2;
  
      // Génération des matchs (logique locale)
      for (let round = 0; round < totalRounds; round++) {
        for (let i = 0; i < halfSize; i++) {
          const teamA = teamList[i];
          const teamB = teamList[teamList.length - 1 - i];
  
          if (teamA !== "BYE" && teamB !== "BYE") {
            const teamAData = teamDetailsMap[teamA] || {};
            const teamBData = teamDetailsMap[teamB] || {};
  
            const matchData = {
              teamA,
              teamB,
              source: 'league',
              date: matchDate.toISOString().split("T")[0],
              time: leagueInfo.time,
              location: leagueInfo.location,
              terrain: terrainToggle ? leagueInfo.terrain1 : leagueInfo.terrain2,
              joueur1A: {
                nom: teamAData.joueur1?.nom || '',
                prenom: teamAData.joueur1?.prenom || '',
                phone: teamAData.joueur1?.phone || '',
                elo: Number(teamAData.joueur1?.elo || 0),
              },
              joueur2A: {
                nom: teamAData.joueur2?.nom || '',
                prenom: teamAData.joueur2?.prenom || '',
                phone: teamAData.joueur2?.phone || '',
                elo: Number(teamAData.joueur2?.elo || 0),
              },
              joueur1B: {
                nom: teamBData.joueur1?.nom || '',
                prenom: teamBData.joueur1?.prenom || '',
                phone: teamBData.joueur1?.phone || '',
                elo: Number(teamBData.joueur1?.elo || 0),
              },
              joueur2B: {
                nom: teamBData.joueur2?.nom || '',
                prenom: teamBData.joueur2?.prenom || '',
                phone: teamBData.joueur2?.phone || '',
                elo: Number(teamBData.joueur2?.elo || 0),
              },
            };
  
            newMatches.push(matchData);
            terrainToggle = !terrainToggle;
          }
        }
  
        // Rotation des équipes
        const lastTeam = teamList.pop();
        teamList.splice(1, 0, lastTeam);
  
        // Ajout 7 jours
        matchDate.setDate(matchDate.getDate() + 7);
      }
  
      // Sauvegarde via Cloud Function
      await saveMatchesViaCloudFunction(newMatches);
      
      // Met à jour l'état local
      setMatches(newMatches);
      console.log("✅ Matchs générés et sauvegardés:", newMatches.length);
  
    } catch (error) {
      console.error("❌ Erreur lors de la génération des matchs :", error);
    }
  };
  
  

  const groupMatchesByWeek = () => {
    const totalMatches = matches.length;
    const numberOfWeeks = Math.ceil(totalMatches / 2); // 2 matchs par semaine
    const grouped = Array.from({ length: numberOfWeeks }, () => []);
  
    matches.forEach((match, index) => {
      const weekIndex = Math.floor(index / 2);
      grouped[weekIndex].push(match);
    });
  
    setGroupedMatches(grouped);
  };
  

  const exportToPDF = async () => {
    console.log('exportToPDF appelé');

    const doc = new jsPDF();
    doc.setFontSize(18);
  
    // Texte principal avec style
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('Calendrier de la Ligue', 20, 20);
  
    // Ajout d'un soulignement
    const textWidth = doc.getTextWidth('Calendrier de la Ligue');
    doc.setDrawColor(128, 0, 32); // Couleur de la ligne (rouge)
    doc.setLineWidth(1);
    doc.line(20, 22, 20 + textWidth, 22);
  
    // Charger et ajouter le logo
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
      });
    };
  
    try {
      const logoPath = '/logo2.jpeg'; // Chemin vers le logo
      console.log('Chargement du logo depuis :', logoPath);
      console.log('Tentative de chargement de l’image...');
const image = await loadImage('/logo2.jpg');
console.log('Image chargée :', image);

      console.log('Image chargée :', image);
  
      // Test avec un fond coloré derrière le logo
      // Dessiner un cercle avant d'ajouter le logo
doc.setDrawColor(0, 0, 0); // Noir pour le contour
doc.setFillColor(255, 255, 255); // Blanc pour le fond du cercle
doc.circle(165, 20, 15, 'FD'); // Centre (165, 20), rayon 15, 'FD' = Fill + Draw

// Ajouter le logo au centre du cercle
doc.addImage(image, 'JPEG', 150, 5, 30, 30); // Ajuste la taille et positionne au centre

    } catch (error) {
      console.error('Erreur lors du chargement du logo :', error);
    }
  
    let currentY = 30; // Position initiale Y
  
    groupedMatches.forEach((weekMatches, weekIndex) => {
      doc.setFontSize(16);
      doc.text(`Semaine ${weekIndex + 1}`, 20, currentY);
  
      const tableBody = weekMatches.map((match) => [
        match.teamA,
        match.teamB,
        formatFrenchDate(match.date),
        match.time,
        match.location,
        match.terrain,
      ]);
  
      doc.autoTable({
        head: [['Équipe A', 'Équipe B', 'Date', 'Heure', 'Lieu','Terrain']],
        body: tableBody,
        startY: currentY + 10, // Position de la table
        margin: { top: 10 },
        headStyles: { fillColor: [128, 0, 32], textColor: [255, 255, 255] },
      });
  
      currentY = doc.lastAutoTable.finalY + 10; // Mise à jour de la position Y
    });
  
    doc.output('dataurlnewwindow'); // Ouvre le PDF dans une nouvelle fenêtre pour tester
  };
  

  const formatFrenchDate = (date) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Intl.DateTimeFormat(language, options).format(new Date(date));
  };

  return (

    <>
      <Helmet>
        <title>{t('calendarTitle')}</title>
        <meta
          name="description"
          content={`Découvrez le calendrier complet de la Ligue ${leagueId} avec les matchs, lieux et horaires.`}
        />
        <meta
          name="keywords"
          content="calendrier ligue, calendrier matchs Padel, Padel Mendyx, ligue Padel Belgique"
        />
        <meta property="og:title" content={`Calendrier Ligue ${leagueId} | Padel Mendyx`} />
        <meta
          property="og:description"
          content={`Découvrez le calendrier complet de la Ligue ${leagueId} avec les matchs, lieux et horaires.`}
        />
        <meta property="og:image" content="/calendar-league-preview.jpg" />
        <meta property="og:url" content={`https://padelmedyx.com/league/${leagueId}/calendar`} />
        <link rel="canonical" href={`https://padelmedyx.com/league/${leagueId}/calendar`} />
      </Helmet>


    <section className="container mx-auto my-24 px-4">
      <div className="flex justify-center mb-8">
      <button
  onClick={() => {
    console.log('Bouton cliqué');
    exportToPDF();
  }}
  className="bg-[#800020] text-white px-4 py-2 rounded shadow hover:bg-[#600018] transition flex items-center gap-2"
>
  <FaDownload />
  {t('exportCalendar')}
</button>

      </div>
      <div className="text-center mt-8">
        {groupedMatches.length > 0 ? (
          groupedMatches.map((weekMatches, weekIndex) => (
            <div key={`week-${weekIndex}`} className="mb-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <button
                  onClick={() => toggleWeekCollapse(weekIndex)}
                  className="text-[#800020] hover:scale-110 transition-transform"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-6 w-6 transform transition-transform ${
                      collapsedWeeks[weekIndex] ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <h2 className="text-2xl font-semibold text-[#800020]">
  {t('week')} {weekIndex + 1} ({groupedMatches.length} {groupedMatches.length > 1 ? t('weeks') : t('oneWeek')})
</h2>

              </div>
              {!collapsedWeeks[weekIndex] && (
                <div className="flex flex-wrap gap-8 justify-center">
                  {weekMatches.map((match, index) => (
                    <div
                      key={index}
                      className="flex items-center bg-gradient-to-r from-[#fdf7f7] to-[#fff] shadow-lg rounded-lg border border-gray-300 p-6 w-96 transform transition duration-300 hover:scale-105 hover:shadow-2xl"
                    >
                      <div className="flex-1 text-center">
                      <p className="text-lg font-medium text-gray-600 mb-2">
  {match.teamA}{' '}
  {match.joueur1A?.elo && match.joueur2A?.elo && (
    <span className="text-sm text-gray-500 font-semibold">
      ({Number(match.joueur1A.elo) + Number(match.joueur2A.elo)} {t('pointsShort')})
    </span>
  )}
</p>

                        <p className="text-2xl font-bold text-[#800020] my-4">VS</p>
                        <p className="text-lg font-medium text-gray-600">
  {match.teamB}{' '}
  {match.joueur1B?.elo && match.joueur2B?.elo && (
    <span className="text-sm text-gray-500 font-semibold">
      ({Number(match.joueur1B.elo) + Number(match.joueur2B.elo)} {t('pointsShort')})
    </span>
  )}
</p>

                        <div className="bg-gray-50 p-4 rounded-lg shadow-inner mt-4">
                          <p className="text-lg text-gray-700 mb-2">
                            📅 <span className="font-semibold">{t('date')} :</span>{' '}
                            {formatFrenchDate(match.date)}
                          </p>
                          <p className="text-lg text-gray-700 mb-2">
                            ⏰ <span className="font-semibold">{t('time')} :</span>{' '}
                            {match.time}
                          </p>
                          <p className="text-lg text-gray-700">
                            📍 <span className="font-semibold">{t('location')} :</span>{' '}
                            {match.location}
                          </p>
                          <p className="text-lg text-gray-700">
                          🎾 <span className="font-semibold">{t('court')} :</span>{' '}
                            {match.terrain}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="text-lg text-gray-500">
            {t('matchesPending', { current: teams.length, limit: participantsLimit })}
          </p>
        )}
      </div>
    </section>
    </>
  );
};

export default CalendarLeague;
